import React, { useEffect, useRef, useState } from "react";
import "./OdsDateFilter.css";
import PosCustomIcon from "../../../../../customComponents/PosCustomIcon/PosCustomIcon";
import { XMarkIcon } from "@heroicons/react/24/outline";

// ── helpers ──────────────────────────────────────────────────────────────────

const pad = (n) => String(n).padStart(2, "0");

const formatDisplay = (date) => {
  if (!date) return "";
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
};

const isSameDay = (a, b) =>
  a &&
  b &&
  a.getDate() === b.getDate() &&
  a.getMonth() === b.getMonth() &&
  a.getFullYear() === b.getFullYear();

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// Returns the 6-week grid of Date objects for a given month view
const buildCalendarGrid = (year, month) => {
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const cells = [];

  // prev month tail
  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ date: new Date(year, month - 1, daysInPrevMonth - i), outside: true });
  }
  // current month
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, month, d), outside: false });
  }
  // next month head — fill to complete 6 rows (42 cells)
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ date: new Date(year, month + 1, d), outside: true });
  }

  return cells;
};

// ── component ─────────────────────────────────────────────────────────────────

const OdsDateFilter = ({ selectedDate, onChange, renderDayContents, onMonthChange, onCalendarOpen }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState((selectedDate || today).getFullYear());
  const [viewMonth, setViewMonth] = useState((selectedDate || today).getMonth());

  const wrapperRef = useRef(null);

  // Sync view when selectedDate changes externally
  useEffect(() => {
    if (selectedDate) {
      setViewYear(selectedDate.getFullYear());
      setViewMonth(selectedDate.getMonth());
    }
  }, [selectedDate]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const prevMonth = () => {
    let newMonth, newYear;
    if (viewMonth === 0) {
      newMonth = 11;
      newYear = viewYear - 1;
    } else {
      newMonth = viewMonth - 1;
      newYear = viewYear;
    }
    setViewMonth(newMonth);
    setViewYear(newYear);
    onMonthChange?.(new Date(newYear, newMonth, 1));
  };

  const nextMonth = () => {
    let newMonth, newYear;
    if (viewMonth === 11) {
      newMonth = 0;
      newYear = viewYear + 1;
    } else {
      newMonth = viewMonth + 1;
      newYear = viewYear;
    }
    setViewMonth(newMonth);
    setViewYear(newYear);
    onMonthChange?.(new Date(newYear, newMonth, 1));
  };

  const handleDayClick = (cell) => {
    const d = new Date(cell.date);
    d.setHours(0, 0, 0, 0);
    onChange(d);
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setOpen(false);
  };

  const handleToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    onChange(d);
    setOpen(false);
  };

  const grid = buildCalendarGrid(viewYear, viewMonth);

  return (
    <div className="ods-date-filter-wrapper" ref={wrapperRef}>
      {/* ── Trigger pill ── */}
      <div className="ods-date-filter-pill" onClick={() => {
        const willOpen = !open;
        setOpen((p) => !p);
        if (willOpen) {
          onCalendarOpen?.(new Date(viewYear, viewMonth, 1));
        }
      }}>
        {/* Left calendar icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#90A1B9"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-calendar-days pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
          aria-hidden="true"
        >
          <path d="M8 2v4"></path>
          <path d="M16 2v4"></path>
          <rect width="18" height="18" x="3" y="4" rx="2"></rect>
          <path d="M3 10h18"></path>
          <path d="M8 14h.01"></path>
          <path d="M12 14h.01"></path>
          <path d="M16 14h.01"></path>
          <path d="M8 18h.01"></path>
          <path d="M12 18h.01"></path>
          <path d="M16 18h.01"></path>
        </svg>

        <div
          className="ods-date-filter-text textAlignCorrection"
          style={{ display: "flex", alignItems: "center", gap: "6px" }}
        >
          {isSameDay(selectedDate || today, today) ? (
            "Today"
          ) : (
            <>
              {formatDisplay(selectedDate)}
              <PosCustomIcon
                icon={<XMarkIcon />}
                height={"14px"}
                width={"14px"}
                pointer={true}
                onClickFunction={(e) => {
                  e.stopPropagation();
                  handleToday();
                }}
              />
            </>
          )}
        </div>
      </div>

      {/* ── Dropdown calendar ── */}
      {open && (
        <div className="ods-date-dropdown">
          {/* Header */}
          <div className="ods-date-header">
            <span className="ods-date-month-label">
              {MONTHS[viewMonth]}, {viewYear}
            </span>
            <div className="ods-date-nav">
              <button className="ods-date-nav-btn" onClick={prevMonth} title="Previous month">
                ↑
              </button>
              <button className="ods-date-nav-btn" onClick={nextMonth} title="Next month">
                ↓
              </button>
            </div>
          </div>

          {/* Day-of-week labels */}
          <div className="ods-date-day-labels">
            {DAYS.map((d) => (
              <span key={d} className="ods-date-day-label">
                {d}
              </span>
            ))}
          </div>

          {/* Grid */}
          <div className="ods-date-grid">
            {grid.map((cell, i) => {
              const isSelected = isSameDay(cell.date, selectedDate);
              const isToday = isSameDay(cell.date, today);

              let cellClass = "ods-date-cell";
              if (cell.outside) cellClass += " ods-date-cell-outside";
              if (isSelected) cellClass += " ods-date-cell-selected";
              else if (isToday) cellClass += " ods-date-cell-today";

              return (
                <div
                  key={i}
                  className={cellClass}
                  onClick={() => !cell.outside && handleDayClick(cell)}
                  style={{ position: "relative" }}
                >
                  {renderDayContents
                    ? renderDayContents(cell.date.getDate(), cell.date)
                    : cell.date.getDate()}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="ods-date-footer">
            <button className="ods-date-footer-btn" onClick={handleClear}>
              Clear
            </button>
            <button className="ods-date-footer-btn" onClick={handleToday}>
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OdsDateFilter;